import { useMemo, useState } from 'react';
import { Row, Col, Select, Space, Typography } from 'antd';
import { TranslationOutlined, NumberOutlined, CheckCircleOutlined, WarningOutlined, PercentageOutlined } from '@ant-design/icons';
import { LoadingSkeleton } from '@/components/LoadingSkeleton';
import { useLanguages } from '@/hooks/useLanguages';
import { useUiPack } from '@/hooks/useUiTranslations';
import { useGlossary } from '@/hooks/useGlossary';
import { KeyListColumn } from './KeyListColumn';
import { KeyEditorColumn } from './KeyEditorColumn';
import { KeyReferenceColumn } from './KeyReferenceColumn';
import { BulkTranslateButton } from './BulkTranslateButton';
import { PageHero, HeroChip } from '@/components/PageHero';
import { KpiTile } from '@/components/KpiTile';

const { Text } = Typography;

export function TranslationEditorPage() {
  const langsQ = useLanguages();
  const langs = useMemo(() => langsQ.data ?? [], [langsQ.data]);
  const nonBundled = useMemo(() => langs.filter((l) => !l.is_bundled), [langs]);
  const [targetLang, setTargetLang] = useState<string>(nonBundled[0]?.code ?? '');
  const sourceLang = 'en';

  const sourcePackQ = useUiPack(sourceLang);
  const targetPackQ = useUiPack(targetLang || null);
  const glossaryQ = useGlossary();

  const sourceValues = useMemo(() => sourcePackQ.data?.translations ?? {}, [sourcePackQ.data]);
  const targetValues = useMemo(() => targetPackQ.data?.translations ?? {}, [targetPackQ.data]);
  const allKeys = useMemo(() => {
    const s = new Set([...Object.keys(sourceValues), ...Object.keys(targetValues)]);
    return Array.from(s);
  }, [sourceValues, targetValues]);

  const [selectedKey, setSelectedKey] = useState<string | null>(null);

  const totalKeys = allKeys.length;
  const translated = allKeys.filter((k) => (targetValues[k] ?? '').trim() !== '').length;
  const missing = totalKeys - translated;
  const pct = totalKeys === 0 ? 0 : Math.round((translated / totalKeys) * 100);

  const otherLangsForKey = useMemo(() => {
    if (!selectedKey) return [];
    return langs
      .filter((l) => l.code !== targetLang && l.code !== sourceLang)
      .map((l) => ({ lang_code: l.code, value: '' }))
      .filter((x) => x.value !== '');
  }, [selectedKey, langs, targetLang, sourceLang]);

  const loading = langsQ.isLoading || sourcePackQ.isLoading || targetPackQ.isLoading;

  return (
    <div>
      <PageHero
        icon={<TranslationOutlined />}
        title="Translations"
        subtitle="Edit UI-string translations per language"
        from="#0d9488" to="#10b981"
        chips={[
          <HeroChip key="translated">{translated}/{totalKeys} translated</HeroChip>,
          <HeroChip key="pct">{pct}%</HeroChip>,
        ]}
        actions={
          <Space>
            <Select
              value={targetLang || undefined}
              onChange={setTargetLang}
              placeholder="Target language"
              options={nonBundled.map((l) => ({ value: l.code, label: `${l.code} — ${l.name_native}` }))}
              style={{ minWidth: 220 }}
            />
            {targetLang && <BulkTranslateButton targetLang={targetLang} />}
          </Space>
        }
      />

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
        <KpiTile tone="sky" icon={<NumberOutlined />} label="Total keys" value={totalKeys} />
        <KpiTile tone="green" icon={<CheckCircleOutlined />} label="Translated" value={translated} />
        <KpiTile tone="amber" icon={<WarningOutlined />} label="Missing" value={missing} />
        <KpiTile tone="indigo" icon={<PercentageOutlined />} label="Coverage" value={`${pct}%`} />
      </div>

      {loading ? (
        <LoadingSkeleton rows={8} />
      ) : !targetLang ? (
        <Text type="secondary">Add at least one non-bundled language to begin translating.</Text>
      ) : (
        <Row gutter={16}>
          <Col span={6}>
            <KeyListColumn
              allKeys={allKeys}
              sourceValues={sourceValues}
              targetValues={targetValues}
              selectedKey={selectedKey}
              onSelect={setSelectedKey}
            />
          </Col>
          <Col span={12}>
            <KeyEditorColumn
              targetLang={targetLang}
              sourceLang={sourceLang}
              selectedKey={selectedKey}
              sourceValue={selectedKey ? (sourceValues[selectedKey] ?? '') : ''}
              targetValue={selectedKey ? (targetValues[selectedKey] ?? '') : ''}
            />
          </Col>
          <Col span={6}>
            <KeyReferenceColumn
              sourceText={selectedKey ? (sourceValues[selectedKey] ?? '') : ''}
              glossary={glossaryQ.data ?? []}
              targetLang={targetLang}
              otherLangs={otherLangsForKey}
            />
          </Col>
        </Row>
      )}
    </div>
  );
}
